import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, Injectable, ErrorHandler } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { IMaskModule } from 'angular-imask';

import { AppComponent } from './app.component';
import { BuyWithin90Component } from './buy-within-90/buy-within-90.component';
import { BuyWithin90FormComponent } from './buy-within-90/buy-within-90-form/buy-within-90-form.component';
import { EmailVerificationComponent } from './email-verification/email-verification.component';
import { OneToOneConsentComponent } from './one-to-one-consent/one-to-one-consent.component';
import { FruHttpService } from '../../../services/fru-http.service';
import { AppRoutingModule } from './/app-routing.module';
import { LeadFormComponent } from './email-verification/lead-form/lead-form.component';
import { ConsentFormComponent } from './one-to-one-consent/consent-form/consent-form.component';
import { FormLandingComponent } from './form-landing/form-landing.component';
import { LenderListingComponent } from './form-landing/fru/lender-listings/lender-listing/lender-listing.component';
import { LenderListingsComponent } from './form-landing/fru/lender-listings/lender-listings.component';
import { FormLandingHeaderComponent } from './form-landing/fru/form-landing-header/form-landing-header.component';
import { FormLandingHeaderDirective } from './form-landing/form-landing-header.directive';
import { FormLandingFooterComponent } from './form-landing/fru/form-landing-footer/form-landing-footer.component';
import { FormLandingFooterDirective } from './form-landing/form-landing-footer.directive';
import { FormLandingBodyComponent } from './form-landing/fru/form-landing-body/form-landing-body.component';
import { FormLandingBodyDirective } from './form-landing/form-landing-body.directive';
import { TrackingCodeComponent } from './form-landing/tracking-code/tracking-code.component';
import { MMLandingBodyComponent } from './form-landing/mm/mmlanding-body/mmlanding-body.component';
import { MMLandingFooterComponent } from './form-landing/mm/mmlanding-footer/mmlanding-footer.component';
import { MMLandingHeaderComponent } from './form-landing/mm/mmlanding-header/mmlanding-header.component';
import { BestLandingBodyComponent } from './form-landing/best/best-landing-body/best-landing-body.component';
import { BestLandingFooterComponent } from './form-landing/best/best-landing-footer/best-landing-footer.component';
import { BestLandingHeaderComponent } from './form-landing/best/best-landing-header/best-landing-header.component';
import { CookieService } from 'ngx-cookie-service';
import { ScriptTagComponent } from '../../../shared/script-tag/script-tag.component';
import { RatesFormComponent } from './rates-form/rates-form.component';
import { StepComponent } from './rates-form/step/step.component';
import { PropertyTypeComponent } from './rates-form/steps/property-type/property-type.component';
import { PropertyUseComponent } from './rates-form/steps/property-use/property-use.component';
import { ButtonStepComponent } from './rates-form/step/button-step/button-step.component';
import { BankruptcyComponent } from './rates-form/steps/bankruptcy/bankruptcy.component';
import { BankruptcyRecencyComponent } from './rates-form/steps/bankruptcy-recency/bankruptcy-recency.component';
import { EmploymentComponent } from './rates-form/steps/employment/employment.component';
import { LoanReasonComponent } from './rates-form/steps/loan-reason/loan-reason.component';
import { CashOutComponent } from './rates-form/steps/cash-out/cash-out.component';
import { CashOutReasonComponent } from './rates-form/steps/cash-out-reason/cash-out-reason.component';
import { SliderStepComponent } from './rates-form/step/slider-step/slider-step.component';
import { PropertyValueComponent } from './rates-form/steps/property-value/property-value.component';
import { FirstMortgageBalanceComponent } from './rates-form/steps/first-mortgage-balance/first-mortgage-balance.component';
import { CurrentFHAComponent } from './rates-form/steps/current-fha/current-fha.component';
import { CreditRatingComponent } from './rates-form/steps/credit-rating/credit-rating.component';
import { MultiInputStepComponent } from './rates-form/step/multi-input-step/multi-input-step.component';
import { SelectStepComponent } from './rates-form/step/select-step/select-step.component';
import { ZipCodeStreetAddressComponent } from './rates-form/steps/zip-code-street-address/zip-code-street-address.component';
import { VeteranMilitaryComponent } from './rates-form/steps/veteran-military/veteran-military.component';
import { CurrentVAComponent } from './rates-form/steps/current-va/current-va.component';
import { FirstNameLastNameComponent } from './rates-form/steps/first-name-last-name/first-name-last-name.component';
import { InputStepComponent } from './rates-form/step/input-step/input-step.component';
import { EmailComponent } from './rates-form/steps/email/email.component';
import { PhoneComponent } from './rates-form/steps/phone/phone.component';
import { NewHomeValueComponent } from './rates-form/steps/new-home-value/new-home-value.component';
import { EstimatedDownPaymentComponent } from './rates-form/steps/estimated-down-payment/estimated-down-payment.component';
import { ZipCodeComponent } from './rates-form/steps/zip-code/zip-code.component';
import { SliderLabelComponent } from './rates-form/step/slider-step/slider-label/slider-label.component';
import { FormFlowShellComponent } from './form-flow-shell/form-flow-shell.component';
import { BuyingProcessComponent } from './rates-form/steps/buying-process/buying-process.component';
import { FirstTimeBuyerComponent } from './rates-form/steps/first-time-buyer/first-time-buyer.component';
import { DaysFromBuyingComponent } from './rates-form/steps/days-from-buying/days-from-buying.component';
import { FoundHomeComponent } from './rates-form/steps/found-home/found-home.component';
import { SignedAgreementComponent } from './rates-form/steps/signed-agreement/signed-agreement.component';
import * as Sentry from '@sentry/browser';
import { PremiumQuestionsComponent } from './premium-questions/premium-questions.component';
import { PremiumLeadFormComponent } from './premium-questions/premium-lead-form/premium-lead-form.component';
import { BestService } from '../../../services/best.service';
import { MaximumHomeEquityComponent } from './rates-form/steps/maximum-home-equity/maximum-home-equity.component';
import { LoanPurposeComponent } from './rates-form/steps/loan-purpose/loan-purpose.component';
import { NeustarComponent  } from './rates-form/steps/neustar/neustar.component';
import { ErrorSrcDirective } from '../../../shared/error-src.directive';
import { CustomerReviewComponent } from './form-landing/customer-review/customer-review.component';
import { DigitOnlyDirective } from './directives/digit-only.directive';
import { SliderProgressDirective } from './directives/slider-progress.directive';
import { APP_BASE_HREF } from '@angular/common';
import { TimeFrameComponent } from './rates-form/steps/time-frame/time-frame.component';
import { PropertyStatusComponent } from './rates-form/steps/property-status/property-status.component';
import { LinkClickModalComponent } from './rates-form/link-click-modal/link-click-modal.component';
import { CommunicationService } from '../../../services/communication.service';
import { SlideEntryComponent } from './form-landing/customer-review/slide-entry/slide-entry.component';
import { CustomStarRatingComponent } from './form-landing/customer-review/custom-star-rating/custom-star-rating.component';
import { StatesDropdownComponent } from '../../../shared/states-dropdown/states-dropdown.component';
import { PropertyTypeConfirmationComponent } from './rates-form/steps/property-type-confirmation/property-type-confirmation.component';
import { ProgressBarComponent } from './rates-form/progress-bar/progress-bar.component';
import { StateRateInfoComponent } from './form-landing/state-rate-info/state-rate-info.component';
import { TrustedPartnersComponent } from './form-landing/trusted-partners/trusted-partners.component';
import { TrustedPartnerComponent } from './form-landing/trusted-partner/trusted-partner.component';
import { ProductOptionsComponent } from './form-landing/product-options/product-options/product-options.component';
import { ProductInfoComponent } from './form-landing/product-options/product-info/product-info.component';
import { ProductOptionComponent } from './form-landing/product-options/product-option/product-option.component';
import { HaveExistingMortgageComponent } from './rates-form/steps/have-existing-mortgage/have-existing-mortgage.component';
import { LendersCountComponent } from './rates-form/steps/lenders-count/lenders-count.component';


// Disable sentry on development domains
const match = /(.+)\-www\.(.+)/.test(window.location.hostname);

if(!match) {
  Sentry.init({
    dsn: 'https://97e2d22d850e434f9ac90e6b9ccc8419@sentry.io/1363241',
  });
}

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() { }
  handleError(error) {
    if(!match) {
      const exception = error.originalError || error.error || error.message || error;
      Sentry.captureException(exception);
    }
    throw error;
  }
}

@NgModule({
    declarations: [
        AppComponent,
        BuyWithin90Component,
        BuyWithin90FormComponent,
        EmailVerificationComponent,
        LeadFormComponent,
        FormLandingComponent,
        FormLandingHeaderComponent,
        FormLandingHeaderDirective,
        FormLandingFooterComponent,
        FormLandingFooterDirective,
        FormLandingBodyComponent,
        FormLandingBodyDirective,
        LenderListingComponent,
        LenderListingsComponent,
        TrackingCodeComponent,
        MMLandingBodyComponent,
        MMLandingFooterComponent,
        MMLandingHeaderComponent,
        BestLandingBodyComponent,
        BestLandingFooterComponent,
        BestLandingHeaderComponent,
        ScriptTagComponent,
        RatesFormComponent,
        StepComponent,
        PropertyTypeComponent,
        PropertyUseComponent,
        ButtonStepComponent,
        BankruptcyComponent,
        BankruptcyRecencyComponent,
        EmploymentComponent,
        LoanReasonComponent,
        CashOutComponent,
        CashOutReasonComponent,
        SliderStepComponent,
        PropertyValueComponent,
        FirstMortgageBalanceComponent,
        CurrentFHAComponent,
        CreditRatingComponent,
        MultiInputStepComponent,
        SelectStepComponent,
        ZipCodeStreetAddressComponent,
        VeteranMilitaryComponent,
        CurrentVAComponent,
        FirstNameLastNameComponent,
        InputStepComponent,
        EmailComponent,
        PhoneComponent,
        BuyingProcessComponent,
        NewHomeValueComponent,
        EstimatedDownPaymentComponent,
        ZipCodeComponent,
        SliderLabelComponent,
        FormFlowShellComponent,
        BuyingProcessComponent,
        FirstTimeBuyerComponent,
        DaysFromBuyingComponent,
        FoundHomeComponent,
        SignedAgreementComponent,
        PremiumQuestionsComponent,
        PremiumLeadFormComponent,
        MaximumHomeEquityComponent,
        HaveExistingMortgageComponent,
        LoanPurposeComponent,
        NeustarComponent,
        ErrorSrcDirective,
        CustomerReviewComponent,
        DigitOnlyDirective,
        SliderProgressDirective,
        TimeFrameComponent,
        PropertyStatusComponent,
        LinkClickModalComponent,
        SlideEntryComponent,
        CustomStarRatingComponent,
        StatesDropdownComponent,
        PropertyTypeConfirmationComponent,
        ProgressBarComponent,
        StateRateInfoComponent,
        TrustedPartnersComponent,
        TrustedPartnerComponent,
        ProductOptionsComponent,
        ProductInfoComponent,
        ProductOptionComponent,
        OneToOneConsentComponent,
        ConsentFormComponent,
        LendersCountComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        ReactiveFormsModule,
        IMaskModule,
        AppRoutingModule,
    ],
    providers: [
        CommunicationService,
        FruHttpService,
        CookieService,
        BestService,
        [{ provide: ErrorHandler, useClass: SentryErrorHandler }],
        [{ provide: APP_BASE_HREF, useValue: '/communication/'}]
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }

